import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../../firebase";
import {
  doc,
  onSnapshot,
  collection,
  query,
  where,
  onSnapshot as onColSnap,
} from "firebase/firestore";

function maskMobile(m) {
  if (!m) return "";
  const s = String(m).replace(/\D/g, "");
  if (s.length <= 4) return "****";
  return s.slice(0, 2) + "****" + s.slice(-2);
}
function symbolOf(code) {
  switch (code) {
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    case "INR":
      return "₹";
    case "LKR":
      return "Rs";
    default:
      return code || "$";
  }
}

export default function PublicCardViewer({ user }) {
  const { cardId } = useParams();
  const [card, setCard] = useState(null);
  const [buyersByNumber, setBuyersByNumber] = useState({}); // { num: {name, mobile} }
  const [holdsSet, setHoldsSet] = useState(new Set()); // numbers currently on hold

  // subscribe card
  useEffect(() => {
    if (!cardId) return;
    const unsub = onSnapshot(doc(db, "cards", cardId), (d) => {
      setCard(d.exists() ? { id: d.id, ...d.data() } : null);
    });
    return () => unsub();
  }, [cardId]);

  // subscribe purchased (payment_approved) tickets
  useEffect(() => {
    if (!cardId) return;
    const qPaid = query(
      collection(db, "tickets"),
      where("cardId", "==", cardId),
      where("status", "==", "payment_approved")
    );
    const unPaid = onColSnap(qPaid, (snap) => {
      const map = {};
      snap.forEach((d) => {
        const t = d.data();
        const who = {
          name: t.userName || (t.userUid ? t.userUid.slice(0, 6) : "Player"),
          mobile: maskMobile(t.userMobile),
        };
        (t.numbers || []).forEach((n) => {
          map[n] = who;
        });
      });
      setBuyersByNumber(map);
    });
    return () => unPaid();
  }, [cardId]);

  // subscribe holds (agent_approved, not yet paid)
  useEffect(() => {
    if (!cardId) return;
    const qHold = query(
      collection(db, "tickets"),
      where("cardId", "==", cardId),
      where("status", "==", "agent_approved")
    );
    const unHold = onColSnap(qHold, (snap) => {
      const set = new Set();
      snap.forEach((d) => {
        const t = d.data();
        (t.numbers || []).forEach((n) => set.add(n));
      });
      setHoldsSet(set);
    });
    return () => unHold();
  }, [cardId]);

  const filledSet = useMemo(() => new Set(card?.filled || []), [card?.filled]);

  return (
    <div className="max-w-5xl mx-auto mt-8 md:mt-16">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">
          Card #{String(cardId || "").slice(0, 8)}…
        </h1>
        <div className="text-sm text-slate-300">
          Price: {symbolOf(card?.currency)}
          {Number(card?.pricePerNumber || 0).toFixed(2)} · Payout{" "}
          {card?.payoutPct ?? 80}%
        </div>
      </div>

      {/* Legend */}
      <div className="mb-3 text-xs text-slate-300 flex items-center gap-4">
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-3.5 h-3.5 rounded bg-lime-400" />{" "}
          Bought
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-3.5 h-3.5 rounded bg-amber-400" /> On
          Hold
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-3.5 h-3.5 rounded bg-slate-700" />{" "}
          Available
        </span>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-10 gap-1">
        {[...Array(100).keys()].map((n) => {
          const buyer = buyersByNumber[n];
          const isBought = filledSet.has(n) || !!buyer;
          const isHold = !isBought && holdsSet.has(n);
          const cls = isBought
            ? "bg-lime-400 text-black font-bold"
            : isHold
            ? "bg-amber-400 text-black"
            : "bg-slate-700 text-white/90";

          return (
            <div
              key={n}
              className={`h-10 rounded text-[11px] leading-10 text-center relative ${cls}`}
            >
              {String(n).padStart(2, "0")}
              {isBought && (
                <div className="absolute inset-x-0 -bottom-5 text-[10px] leading-4 text-black/80 font-semibold">
                  <span className="bg-yellow-300/80 rounded px-1">
                    {buyer
                      ? `${buyer.name} ${
                          buyer.mobile ? `(${buyer.mobile})` : ""
                        }`
                      : "Sold"}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Card meta + back links */}
      <div className="mt-6 text-sm text-slate-300 flex items-center gap-4 flex-wrap">
        <div>
          Status: <b className="capitalize">{card?.status || "-"}</b>
        </div>
        {card?.drawAt && (
          <div>
            Draw:{" "}
            {new Date(card.drawAt?.toDate?.() || card.drawAt).toLocaleString()}
          </div>
        )}
        {card?.winningNumber != null && (
          <div>
            Winning:{" "}
            <span className="px-2 py-0.5 rounded bg-yellow-300 text-black font-bold">
              {String(card.winningNumber).padStart(2, "0")}
            </span>
          </div>
        )}
        <Link to="/my-tickets" className="ml-auto underline">
          My Tickets
        </Link>
      </div>
    </div>
  );
}
